import type { UserRole } from '../api/schema';

/**
 * UI-side mirror of the backend RBAC (architecture §13). The server is always the
 * authority — these helpers only decide what the UI OFFERS, so it never shows an
 * action the API would refuse. `permissions` on the principal is the real source
 * of truth for fine-grained gating; role groups here drive coarse nav gating.
 */

/** The two platform roles (tenant_id = null; reach tenant data via elevation). */
export const PLATFORM_ROLES: readonly UserRole[] = ['system_admin', 'partner_admin'];

/** Roles allowed into the ADMIN nav group and /admin routes. */
export const ADMIN_ROLES: readonly UserRole[] = ['system_admin', 'partner_admin'];

/** Roles allowed into the Settings nav item and /settings routes — mirrors
 * the backend's `_TENANT_MANAGEMENT` (`tenant_settings:read` and friends are
 * all admin-only; analyst/viewer have nothing to see under Settings). */
export const SETTINGS_ROLES: readonly UserRole[] = ['system_admin', 'partner_admin', 'tenant_admin'];

/** Roles that may use the real tenant switcher (act across tenants). */
export const TENANT_SWITCH_ROLES: readonly UserRole[] = ['system_admin', 'partner_admin'];

/**
 * Whether to offer the "Ask Docsight" assistant. Everyone except the two platform
 * roles: it answers questions about one tenant's own documents, and a platform role
 * has no tenant of its own. Mirrors the backend's `chat:use` grant, which refuses
 * them too — this only decides what the UI offers.
 */
export function canUseChat(role: UserRole | null | undefined): boolean {
  return role != null && !PLATFORM_ROLES.includes(role);
}

export function isAdminRole(role: UserRole | null | undefined): boolean {
  return role != null && ADMIN_ROLES.includes(role);
}

export function canSwitchTenant(role: UserRole | null | undefined): boolean {
  return role != null && TENANT_SWITCH_ROLES.includes(role);
}

/**
 * Where a role lands after signing in (and where `/` sends an authenticated
 * user). Platform roles have no tenant, so they go to their admin home rather
 * than the tenant dashboard; tenant roles go to the dashboard.
 */
export function landingPathForRole(role: UserRole | null | undefined): string {
  if (role === 'system_admin') return '/admin/system';
  if (role === 'partner_admin') return '/admin/partner';
  return '/dashboard';
}

/** Human-readable role names for the UI (spec §4.1). */
export const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'System admin',
  partner_admin: 'Partner admin',
  tenant_admin: 'Tenant admin',
  tenant_analyst: 'Analyst',
  tenant_viewer: 'Viewer',
  ingestion_service: 'Ingestion service',
};

export function roleLabel(role: UserRole): string {
  return ROLE_LABELS[role];
}
